import { Graphics } from 'pixi.js';
import type { DesensitizeObject } from '../sim/minigames/desensitize';

export type ArrowDirection = 'up' | 'left' | 'right';
export type TrustObstacleKind = 'puddle' | 'tarp' | 'bridge' | 'barrels';

/** Chunky code-native arrows that stay readable at the game's integer scale. */
export function drawTrainingArrow(
  graphics: Graphics,
  x: number,
  y: number,
  direction: ArrowDirection,
  color: number,
  size = 1,
): void {
  const s = size;
  const points = direction === 'up'
    ? [x, y - 10 * s, x + 10 * s, y, x + 4 * s, y, x + 4 * s, y + 10 * s, x - 4 * s, y + 10 * s, x - 4 * s, y, x - 10 * s, y]
    : direction === 'left'
      ? [x - 10 * s, y, x, y - 10 * s, x, y - 4 * s, x + 10 * s, y - 4 * s, x + 10 * s, y + 4 * s, x, y + 4 * s, x, y + 10 * s]
      : [x + 10 * s, y, x, y - 10 * s, x, y - 4 * s, x - 10 * s, y - 4 * s, x - 10 * s, y + 4 * s, x, y + 4 * s, x, y + 10 * s];
  graphics.poly(points).fill(0x241a11);
  const inner = points.map((value, index) => index % 2 === 0 ? x + (value - x) * 0.72 : y + (value - y) * 0.72);
  graphics.poly(inner).fill(color);
}

/** Four distinct pen hazards. These intentionally use the same low-res,
 * outlined geometry as the sprite sheet instead of generic warning icons. */
export function drawSpookObject(
  graphics: Graphics,
  kind: DesensitizeObject,
  x: number,
  groundY: number,
  mirror = false,
  alpha = 1,
): void {
  const flip = mirror ? -1 : 1;
  const fill = (color: number) => ({ color, alpha });
  if (kind === 'tarp') {
    graphics
      .rect(x - 2, groundY - 36, 4, 36).fill(fill(0x352818))
      .poly([
        x + flip * 2, groundY - 34,
        x + flip * 26, groundY - 29,
        x + flip * 21, groundY - 12,
        x + flip * 4, groundY - 18,
      ]).fill(fill(0x2f668c))
      .poly([
        x + flip * 5, groundY - 30,
        x + flip * 21, groundY - 27,
        x + flip * 18, groundY - 23,
        x + flip * 6, groundY - 25,
      ]).fill(fill(0x6ba5c9));
    return;
  }
  if (kind === 'goose') {
    const neckX = mirror ? x - 14 : x + 8;
    graphics
      .ellipse(x, groundY - 10, 16, 9).fill(fill(0xede7d5))
      .rect(neckX, groundY - 29, 6, 20).fill(fill(0xede7d5))
      .ellipse(x + flip * 13, groundY - 31, 6, 5).fill(fill(0xede7d5))
      .poly([x + flip * 18, groundY - 31, x + flip * 27, groundY - 28, x + flip * 18, groundY - 26]).fill(fill(0xd9a441))
      .rect(x - 7, groundY - 2, 2, 7).fill(fill(0x9a6e32))
      .rect(x + 5, groundY - 2, 2, 7).fill(fill(0x9a6e32))
      .rect(x + flip * 14, groundY - 33, 2, 2).fill(fill(0x241a11));
    return;
  }
  if (kind === 'umbrella') {
    graphics
      .rect(x - 1, groundY - 31, 3, 34).fill(fill(0x352818))
      .poly([
        x - 26, groundY - 29,
        x - 18, groundY - 41,
        x, groundY - 47,
        x + 18, groundY - 41,
        x + 26, groundY - 29,
      ]).fill(fill(0x241a11))
      .poly([
        x - 22, groundY - 31,
        x - 15, groundY - 39,
        x, groundY - 44,
        x + 15, groundY - 39,
        x + 22, groundY - 31,
      ]).fill(fill(0xc4574a))
      .poly([x - 15, groundY - 39, x, groundY - 44, x, groundY - 31]).fill(fill(0xefe8d2))
      .poly([x + 15, groundY - 39, x, groundY - 44, x, groundY - 31]).fill(fill(0xefe8d2));
    return;
  }
  graphics
    .ellipse(x, groundY - 25, 15, 8).fill(fill(0x241a11))
    .poly([x - 15, groundY - 25, x + 15, groundY - 25, x + 11, groundY, x - 11, groundY]).fill(fill(0x6f7680))
    .rect(x - 10, groundY - 21, 20, 4).fill(fill(0x9ba3ad))
    .rect(x - 13, groundY - 3, 26, 4).fill(fill(0x241a11));
}

export function drawTrustObstacle(
  graphics: Graphics,
  kind: TrustObstacleKind,
  x: number,
  groundY: number,
  alpha = 1,
): void {
  const fill = (color: number) => ({ color, alpha });
  if (kind === 'puddle') {
    graphics
      .ellipse(x, groundY, 36, 8).fill(fill(0x294d68))
      .ellipse(x - 6, groundY - 2, 22, 4).fill(fill(0x5d91ae))
      .rect(x + 10, groundY - 5, 12, 2).fill(fill(0x9dc8db));
    return;
  }
  if (kind === 'tarp') {
    graphics
      .poly([x - 34, groundY, x - 26, groundY - 9, x + 30, groundY - 6, x + 36, groundY]).fill(fill(0x23435c))
      .poly([x - 28, groundY - 3, x - 22, groundY - 7, x + 10, groundY - 5, x + 16, groundY - 2]).fill(fill(0x5b9bc0));
    return;
  }
  if (kind === 'bridge') {
    graphics
      .rect(x - 38, groundY - 9, 76, 11).fill(fill(0x3c2b1d))
      .rect(x - 36, groundY - 7, 72, 7).fill(fill(0x9a7042));
    for (let px = x - 31; px < x + 34; px += 10) {
      graphics.rect(px, groundY - 7, 2, 7).fill(fill(0x513a25));
    }
    return;
  }
  for (const bx of [x - 16, x + 12]) {
    graphics
      .ellipse(bx, groundY - 24, 12, 5).fill(fill(0x382319))
      .rect(bx - 12, groundY - 24, 24, 23).fill(fill(0x8e4830))
      .rect(bx - 12, groundY - 19, 24, 3).fill(fill(0xc17a45))
      .rect(bx - 12, groundY - 7, 24, 3).fill(fill(0x513024));
  }
}
